import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Search, LogOut, Settings, User, MessageCircle, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import CommandMenu from './CommandMenu';
import { ACCESS_TOKEN, BRAND_NAME, MERCHANT_DETAILS, MERCHANT_USER_ID } from '../../api/constants';
import { clearAuth } from '../../state/slices/authSlice';
import { clearMerchantDetails } from '../../state/slices/testmodeSlice';
import { client, prodUrl } from '../../api/api';
import { getPublicInfo, getBussinessInfo, getMerchantDetails } from '../../api';

/**
 * ContentHeader - ShadCN-style header row inside content area
 * Contains: Sidebar trigger, page title, search, merchant actions
 */
const ContentHeader = ({ pageTitle }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [storeName, setStoreName] = useState('Store');
  const [legalName, setLegalName] = useState('');
  const [accountManager, setAccountManager] = useState(null);
  const [commandOpen, setCommandOpen] = useState(false);

  useEffect(() => {
    const accessToken = localStorage.getItem(ACCESS_TOKEN);
    if (accessToken) {
      setIsLoggedIn(true);
      
      // Get brand name from localStorage first
      const brandName = localStorage.getItem(BRAND_NAME);
      if (brandName && brandName !== 'null') {
        setStoreName(brandName);
      }

      // Try to get account manager from localStorage
      const merchantDetails = localStorage.getItem(MERCHANT_DETAILS);
      if (merchantDetails && merchantDetails !== 'null') {
        try {
          setAccountManager(JSON.parse(merchantDetails));
        } catch (e) {
          console.error('Failed to parse merchant details:', e);
        }
      }

      // Fetch the actual data from APIs
      fetchMerchantInfo();
    }
  }, []);

  // Listen for Cmd+K to open command menu
  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setCommandOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const fetchMerchantInfo = async () => {
    try {
      // Fetch store name
      const publicInfoResponse = await getPublicInfo();
      if (publicInfoResponse?.status === 200 && publicInfoResponse?.data?.data?.store_name) {
        const apiStoreName = publicInfoResponse.data.data.store_name;
        setStoreName(apiStoreName);
        localStorage.setItem(BRAND_NAME, apiStoreName);
      }

      // Fetch legal name
      const businessInfoResponse = await getBussinessInfo();
      if (businessInfoResponse?.status === 200 && businessInfoResponse?.data?.data?.legal_name) {
        setLegalName(businessInfoResponse.data.data.legal_name);
      }

      // Fetch account manager info
      const merchantUserId = localStorage.getItem(MERCHANT_USER_ID);
      if (merchantUserId) {
        const merchantDetailsResponse = await getMerchantDetails(merchantUserId);
        if (merchantDetailsResponse?.data?.code === 200) {
          const data = merchantDetailsResponse.data.data;
          if (data?.account_manager) {
            setAccountManager(data.account_manager);
            localStorage.setItem(MERCHANT_DETAILS, JSON.stringify(data.account_manager));
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch merchant info:', error);
    }
  };

  const handleLogout = () => {
    client.defaults.baseURL = prodUrl;
    localStorage.clear();
    dispatch(clearAuth());
    dispatch(clearMerchantDetails());
    navigate('/');
  };

  const handleWhatsAppClick = () => {
    if (accountManager?.account_manager_phone_number) {
      const phoneNumber = accountManager.account_manager_phone_number.replace(/\D/g, '');
      window.open(`https://wa.me/${phoneNumber}`, '_blank');
    }
  };

  const handleEmailClick = () => {
    if (accountManager?.account_manager_email) {
      window.location.href = `mailto:${accountManager.account_manager_email}`;
    }
  };

  const getInitial = (name) => {
    if (!name || name === 'null') return 'S';
    return name.charAt(0).toUpperCase();
  };

  const getAvatarColor = (name) => {
    if (!name || name === 'null') return 'bg-indigo-500';
    
    const colors = [
      'bg-indigo-500',
      'bg-violet-500',
      'bg-pink-500',
      'bg-amber-500',
      'bg-emerald-500',
      'bg-blue-500',
      'bg-red-500',
      'bg-teal-500',
    ];
    
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <>
      <header className="flex h-12 shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2 px-4 w-full">
          {/* Left: Sidebar trigger + separator + page title */}
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <h1 className="text-sm font-medium">{pageTitle}</h1>

          {/* Center/Right: Search + actions */}
          <div className="flex-1 flex items-center justify-end gap-2">
            {/* Search button that opens command menu */}
            <Button
              variant="outline"
              className="relative h-8 w-full max-w-[240px] justify-start text-sm text-muted-foreground sm:pr-12 md:max-w-[300px]"
              onClick={() => setCommandOpen(true)}
            >
              <Search className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline-flex">Search...</span>
              <kbd className="pointer-events-none absolute right-1.5 top-1.5 hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
                <span className="text-xs">⌘</span>K
              </kbd>
            </Button>

            {/* Merchant info + user dropdown */}
            {isLoggedIn && (
              <div className="flex items-center gap-2">
                {/* User dropdown - only merchant identity element, no separate badge */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="flex items-center gap-2 px-2 py-1 h-8"
                    >
                      <Avatar className={cn("h-6 w-6 rounded-md", getAvatarColor(storeName))}>
                        <AvatarFallback className="rounded-md bg-transparent text-white text-xs font-semibold">
                          {getInitial(storeName)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm hidden sm:inline-block max-w-[120px] truncate">
                        {storeName}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium">{storeName}</p>
                        {legalName && (
                          <p className="text-xs text-muted-foreground">{legalName}</p>
                        )}
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    
                    {/* Account Manager Info */}
                    {accountManager && (
                      <>
                        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                          Account Manager
                        </DropdownMenuLabel>
                        <DropdownMenuItem disabled className="opacity-100">
                          <User className="mr-2 h-4 w-4" />
                          <span>{accountManager.account_manager_name || 'Your Manager'}</span>
                        </DropdownMenuItem>
                        {accountManager.account_manager_phone_number && (
                          <DropdownMenuItem onClick={handleWhatsAppClick}>
                            <MessageCircle className="mr-2 h-4 w-4" />
                            <span>WhatsApp</span>
                          </DropdownMenuItem>
                        )}
                        {accountManager.account_manager_email && (
                          <DropdownMenuItem onClick={handleEmailClick}>
                            <Mail className="mr-2 h-4 w-4" />
                            <span>Email</span>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                      </>
                    )}

                    <DropdownMenuItem onClick={() => navigate('/settings')}>
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Settings</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600">
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Log out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Command Menu Dialog */}
      <CommandMenu open={commandOpen} onOpenChange={setCommandOpen} />
    </>
  );
};

export default ContentHeader;
